import React from 'react';
import { solutionsData } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

export const Solutions = () => {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <h1 className="text-5xl font-bold text-gray-900">Solutions for Every Business</h1>
            <p className="text-xl text-gray-600">
              Tailored cloud infrastructure solutions designed for your industry and growth stage
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {solutionsData.map((solution) => (
              <Card key={solution.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8 space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900">{solution.title}</h3>
                  <p className="text-gray-600">{solution.description}</p>
                  <div className="space-y-3">
                    {solution.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" className="w-full group/btn" asChild>
                    <Link to="/contact">
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industry-Specific Solutions</h2>
            <p className="text-xl text-gray-600">Proven expertise across multiple sectors</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "FinTech & Banking",
                description: "Secure, compliant infrastructure for financial services with real-time transaction processing",
                features: ["PCI-DSS Compliance", "Real-time processing", "Fraud detection"]
              },
              {
                title: "Healthcare & Life Sciences",
                description: "HIPAA-compliant cloud solutions for healthcare providers and medical technology companies",
                features: ["HIPAA Compliance", "Data privacy", "Secure patient data"]
              },
              {
                title: "E-Commerce & Retail",
                description: "Scalable infrastructure to handle traffic spikes and ensure seamless shopping experiences",
                features: ["Auto-scaling", "CDN integration", "Payment security"]
              },
              {
                title: "Media & Entertainment",
                description: "High-bandwidth infrastructure for content delivery, streaming, and digital media management",
                features: ["Content delivery", "Video streaming", "Media storage"]
              },
              {
                title: "Education Technology",
                description: "Reliable platforms for online learning, virtual classrooms, and educational content delivery",
                features: ["Video conferencing", "LMS integration", "Content delivery"]
              },
              {
                title: "IoT & Smart Devices",
                description: "Edge computing and real-time data processing for connected devices and IoT ecosystems",
                features: ["Edge computing", "Real-time data", "Device management"]
              }
            ].map((useCase, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{useCase.title}</h3>
                  <p className="text-gray-600">{useCase.description}</p>
                  <div className="space-y-2">
                    {useCase.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-700 rounded-full" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl font-bold">Ready to Scale Your Business?</h2>
          <p className="text-xl text-blue-100">
            Let's discuss how our solutions can accelerate your growth
          </p>
          <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100" asChild>
            <Link to="/contact">Schedule a Free Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Solutions;